import { apiSlice } from "app/api/apiSlice.ts";
import type { ErrorData } from "app/types/error.ts";
import type { EnvironmentInfo, HealthInfo } from "./environmentTypes.ts";

export const environmentApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getEnvironment: build.query<EnvironmentInfo | null, void>({
      async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
        const result = await baseQuery("/api/environment");

        if (result.error) {
          const error = result.error as ErrorData;

          if (typeof error === "object" && error !== null) {
            const status = (error as { status?: unknown }).status;
            const originalStatus = (error as { originalStatus?: unknown })
              .originalStatus;

            if (
              (typeof status === "number" && status === 404) ||
              status === "404" ||
              (typeof originalStatus === "number" && originalStatus === 404)
            ) {
              return { data: null };
            }
          }

          return { error: result.error };
        }

        return { data: result.data as EnvironmentInfo };
      },
      providesTags: ["environment"],
    }),
    getHealth: build.query<HealthInfo, void>({
      async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
        const result = await baseQuery("/healthz");

        if (result.error) {
          return { error: result.error };
        }

        const response = result.data as Record<string, unknown>;
        return {
          data: {
            status: typeof response.status === "string" ? response.status : "",
            version:
              typeof response.version === "string" ? response.version : null,
            timestampUtc:
              typeof response.timestampUtc === "string"
                ? response.timestampUtc
                : undefined,
            sessionId:
              typeof response.sessionId === "string"
                ? response.sessionId
                : undefined,
            database:
              typeof response.database === "object" && response.database !== null
                ? {
                    status:
                      typeof (response.database as { status?: unknown })
                        .status === "string"
                        ? ((response.database as { status?: string }).status as string)
                        : "",
                    reason:
                      typeof (response.database as { reason?: unknown })
                        .reason === "string"
                        ? (response.database as { reason: string }).reason
                        : undefined,
                    error:
                      typeof (response.database as { error?: unknown }).error ===
                      "string"
                        ? (response.database as { error: string }).error
                        : undefined,
                  }
                : undefined,
          },
        } satisfies { data: HealthInfo };
      },
    }),
  }),
});

export const { useGetEnvironmentQuery, useGetHealthQuery } =
  environmentApiSlice;
